import { Link } from "react-router-dom";
import { activeListingCount } from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { eur } from "../../lib/format";
import { PageTitle } from "../../components/company/ui";
import { useT } from "../../i18n";

/**
 * "My subscription" (GET /company/subscription). During the free launch every
 * company is on "Free launch access"; afterwards the public plans are shown
 * (subscribing to one is not available online yet).
 */
export default function CompanySubscription() {
  const t = useT();
  const sb = t.company.mySubscription;
  const { state } = useCompany();
  const pricing = state.pricing;

  if (!pricing) {
    return (
      <>
        <PageTitle>{sb.title}</PageTitle>
        <p className="m-0 text-sm text-muted">{sb.loading}</p>
      </>
    );
  }

  const sponsorOptions = pricing.sponsor.options.map((o) => t.company.sponsor.option(o.days, eur(o.price))).join(" · ");

  return (
    <div className="max-w-280">
      <PageTitle>{sb.title}</PageTitle>

      {pricing.mode === "launch" ? (
        <section className="grid gap-6 rounded-card border-2 border-brand bg-white p-6 md:grid-cols-2">
          <div className="flex flex-col items-start">
            <span className="rounded bg-tone-green-bg px-2 py-1 text-xs font-medium text-tone-green-fg">{sb.active}</span>
            <h2 className="m-0 mt-3 text-[26px] font-medium">{sb.launchTitle}</h2>
            <div className="mt-2 text-[15px]">{sb.launchPrice}</div>
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span>{sb.activeListings}</span>
              <b className="font-medium">{sb.usage(activeListingCount(state), pricing.listingLimit)}</b>
            </div>
            <p className="m-0 mt-2 text-[13px] leading-relaxed text-muted">{sb.launchText(pricing.noticeDays)}</p>
          </div>
        </section>
      ) : (
        <section>
          <h2 className="m-0 mb-1 text-lg font-medium">{sb.plansTitle}</h2>
          <p className="m-0 mb-4 text-[13px] text-muted">{sb.plansText}</p>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {pricing.plans?.map((p) => (
              <div key={p.id} className="flex flex-col gap-2.5 rounded-card bg-white p-5">
                <b className="text-lg font-medium">{p.name}</b>
                <div>
                  <span className="text-2xl font-semibold">{eur(p.price)}</span>
                  <span className="text-[13px] text-muted"> {sb.perMonth}</span>
                </div>
                <div className="text-[13px] font-medium">{sb.listingLimit(p.listingLimit)}</div>
                <ul className="m-0 grid list-none gap-1.5 p-0 text-[13px] text-ink-soft">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-brand" aria-hidden="true">
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link
            to="/contact"
            className="mt-4 inline-flex h-10 items-center rounded bg-brand px-4.5 text-[13px] font-medium text-white no-underline hover:bg-brand-dark"
          >
            {sb.contact}
          </Link>
        </section>
      )}

      {pricing.sponsor.enabled && pricing.sponsor.options.length > 0 && (
        <section className="mt-4 flex flex-wrap items-center gap-4 rounded-card bg-white px-5 py-4.5">
          <span className="rounded bg-brand px-2 py-1 text-[11px] font-semibold uppercase tracking-[.06em] text-white">
            {sb.sponsorBadge}
          </span>
          <div className="min-w-55 flex-1">
            <b className="text-sm font-medium">{sb.sponsorTitle}</b>
            <div className="mt-0.5 text-[13px] leading-normal text-muted">{sb.sponsorText(sponsorOptions)}</div>
          </div>
          <Link to="/company/listings" className="text-[13px] font-medium text-brand-link">
            {sb.sponsorCta}
          </Link>
        </section>
      )}

      <p className="mt-4 mb-0 text-xs text-faint">{sb.footnote}</p>
    </div>
  );
}
